import { useMetroHint } from "../../../app/hooks/useFans.js";
import { useDebouncedValue } from "./useDebouncedValue.js";
import styles from "./FanForm.module.scss";

/**
 * The live metro-rollup hint: "→ Will save as Greensboro, NC (audience metro)". The value is
 * SERVER-computed (the same resolution the save uses), so the note always matches what will be stored. Only shown
 * when the typed (city, state) actually rolls up to a different metro — an exact/unmapped city needs no note.
 */
export function MetroHintNote({ city, state }: { city: string; state: string }) {
  // ONE debounce over the PAIR, not one per field. Two independent timers settle microseconds apart, which
  // put a (city, "") query key between the two settled states — a key with no cached data, so the note
  // unmounted mid-type and came back. That reads as a blink while typing, and it made the test flake in CI:
  // the assertion could land in the gap and find no note at all. Debouncing the pair means the key goes
  // straight from the old value to the new one, with no intermediate query.
  const [debouncedCity, debouncedState] = useDebouncedValue(
    `${city.trim()}\u0000${state.trim()}`,
  ).split("\u0000");
  const hint = useMetroHint(debouncedCity, debouncedState);

  if (!hint.data?.mapped) return null;
  const rolled = hint.data.city !== debouncedCity || (hint.data.state || "") !== debouncedState;
  if (!rolled) return null;

  return (
    <p className={styles.metroHint}>
      → Will save as <strong>{hint.data.city}{hint.data.state ? `, ${hint.data.state}` : ""}</strong> (audience metro)
    </p>
  );
}
